import React from 'react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { AlertTriangle, ShieldAlert } from 'lucide-react';

interface CompatibleLift {
  bayId: string;
  bayName: string;
  liftCapacityLbs: number;
  bayType?: string;
}

interface LiftCapacityWarningProps {
  open: boolean;
  onClose: () => void;
  gvwr: number;
  capacity: number;
  compatibleLifts?: CompatibleLift[];
  onSelectLift?: (bayId: string) => void;
}

export function LiftCapacityWarning({
  open,
  onClose,
  gvwr,
  capacity,
  compatibleLifts = [],
  onSelectLift,
}: LiftCapacityWarningProps) {
  return (
    <AlertDialog open={open} onOpenChange={(v) => !v && onClose()}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <div className="flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-destructive" />
            <AlertDialogTitle>Lift Capacity Exceeded</AlertDialogTitle>
          </div>
          <AlertDialogDescription asChild>
            <div className="space-y-3 pt-2">
              <p className="text-sm text-foreground">
                This vehicle cannot be placed on this lift.
              </p>

              <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Vehicle GVWR</span>
                  <span className="font-semibold text-foreground">{gvwr.toLocaleString()} lbs</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Lift capacity</span>
                  <span className="font-semibold text-destructive">{capacity.toLocaleString()} lbs</span>
                </div>
              </div>

              {compatibleLifts.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs font-medium text-muted-foreground">
                    Compatible lifts in this shop:
                  </p>
                  <div className="space-y-1">
                    {compatibleLifts.map((lift) => (
                      <button
                        key={lift.bayId}
                        className="w-full flex items-center justify-between rounded-md border px-3 py-2 text-sm hover:bg-accent/50 transition-colors"
                        onClick={() => onSelectLift?.(lift.bayId)}
                      >
                        <span className="font-medium">{lift.bayName}</span>
                        <span className="text-muted-foreground">
                          {lift.liftCapacityLbs.toLocaleString()} lbs
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {compatibleLifts.length === 0 && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  <span>No compatible lifts found in this shop.</span>
                </div>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <Button variant="outline" size="sm" onClick={onClose}>
            Close
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
